 
import { stateListUS } from 'constants/statesList';
import { UNIT_OF_MEASURE_TYPES, UNIT_TYPE_NAMES } from 'constants/unitTypes';
import { DealType, DealTypeToId } from 'constants/deals';
import { IAdditionalField } from './types';
import { Attributes } from '../constants';

export enum ConfigurationSidebarTabs {
    Attributes = 'Attributes',
    Filters = 'Filters',
}

interface IFilterOption {
    label: string;
    value: string;
}

export const longValueAttributes: Attributes[] = [Attributes.Address, Attributes.Description];

export enum FilterType {
    ContainsExactly = 'contains exactly',
    DoesNotContainExactly = "doesn't contain exactly",
    IsEqualTo = 'is equal to',
    IsNotEqualTo = 'is not equal to',
    IsAnyOf = 'is any of',
    IsNoneOf = 'is none of',
    IsGreaterThan = 'is greater than',
    IsGreaterThanOrEqualTo = 'is greater than or equal to',
    IsLessThan = 'is less than',
    IsLessThanOrEqualTo = 'is less than or equal to',
    IsBetween = 'is between',
    IsKnown = 'is known',
    IsUnknown = 'is unknown',
    IsBefore = 'is before',
    IsAfter = 'is after',
    IsAllTime = 'is all times',
    IsThis = 'is this',
    IsNext = 'is next',
    IsLast = 'is last',
}

enum LeadSource {
    Broker = 'Broker',
    DirectOwnerRelationship = 'Direct owner relationship',
    DirectMarketing = 'Direct marketing',
    OnlinePlatform = 'Online platform',
    Networking = 'Networking',
    Wholesaler = 'Wholesaler',
    MLS = 'MLS',
    PublicRecordsAuction = 'Public records/auction',
    DriveBy = 'Drive-by',
    Other = 'Other',
}

export const LeadSourceToId: Record<LeadSource, number> = {
    [LeadSource.Broker]: 1,
    [LeadSource.DirectOwnerRelationship]: 2,
    [LeadSource.DirectMarketing]: 3,
    [LeadSource.OnlinePlatform]: 4,
    [LeadSource.Networking]: 5,
    [LeadSource.Wholesaler]: 6,
    [LeadSource.MLS]: 7,
    [LeadSource.PublicRecordsAuction]: 8,
    [LeadSource.DriveBy]: 9,
    [LeadSource.Other]: 10,
};

enum LeasingStrategy {
    Speculative = 'Speculative',
    BuildToSuit = 'Build-to-Suit',
}

export const LeasingStrategyToId: Record<LeasingStrategy, number> = {
    [LeasingStrategy.Speculative]: 1,
    [LeasingStrategy.BuildToSuit]: 2,
};

enum Rank {
    High = 'High',
    Medium = 'Medium',
    Low = 'Low',
}

export const RankToId: Record<Rank, number> = {
    [Rank.High]: 1,
    [Rank.Medium]: 2,
    [Rank.Low]: 3,
};

enum Period {
    Day = 'Day',
    Week = 'Week',
    WeekSoFar = 'Week so far',
    Month = 'Month',
    MonthSoFar = 'Month so far',
    Quarter = 'Quarter',
    QuarterSoFar = 'Quarter so far',
    Year = 'Year',
    YearSoFar = 'Year so far',
}

export enum CustomAttributeInputType {
    Text = 'text',
    Number = 'number',
    Amount = 'amount',
    Select = 'select',
    MultiSelect = 'multi_select',
}

export const unitTypeSelectorOptions = [
    { label: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.ACRE].dropdownItemName, value: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.ACRE].dropdownItemName },
    { label: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.GROSS_SF].dropdownItemName, value: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.GROSS_SF].dropdownItemName },
    {
        label: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.RENTABLE_SF].dropdownItemName,
        value: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.RENTABLE_SF].dropdownItemName,
    },
    {
        label: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.RESIDENTIAL_UNIT].dropdownItemName,
        value: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.RESIDENTIAL_UNIT].dropdownItemName,
    },
    { label: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.LOT].dropdownItemName, value: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.LOT].dropdownItemName },
    { label: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.BED].dropdownItemName, value: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.BED].dropdownItemName },
    { label: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.MEGAWATT].dropdownItemName, value: UNIT_TYPE_NAMES[UNIT_OF_MEASURE_TYPES.MEGAWATT].dropdownItemName },
];

export const customAttributeFilterOptions = {
    [CustomAttributeInputType.Text]: [
        FilterType.ContainsExactly,
        FilterType.DoesNotContainExactly,
        FilterType.IsKnown,
        FilterType.IsUnknown,
    ],
    [CustomAttributeInputType.Number]: [
        FilterType.IsEqualTo,
        FilterType.IsNotEqualTo,
        FilterType.IsGreaterThan,
        FilterType.IsGreaterThanOrEqualTo,
        FilterType.IsLessThan,
        FilterType.IsLessThanOrEqualTo,
        FilterType.IsBetween,
        FilterType.IsKnown,
        FilterType.IsUnknown,
    ],
    [CustomAttributeInputType.Amount]: [
        FilterType.IsEqualTo,
        FilterType.IsNotEqualTo,
        FilterType.IsGreaterThan,
        FilterType.IsGreaterThanOrEqualTo,
        FilterType.IsLessThan,
        FilterType.IsLessThanOrEqualTo,
        FilterType.IsBetween,
        FilterType.IsKnown,
        FilterType.IsUnknown,
    ],
    [CustomAttributeInputType.Select]: [FilterType.IsAnyOf, FilterType.IsNoneOf],
    [CustomAttributeInputType.MultiSelect]: [FilterType.IsAnyOf, FilterType.IsNoneOf],
};

export const defaultAttributeFilterOptionGroups = [
    {
        appliedToAttributes: [
            Attributes.State,
            Attributes.Market,
            Attributes.DealType,
            Attributes.AssetClass,
            Attributes.ConstructionType,
            Attributes.Phase,
            Attributes.LeadSource,
            Attributes.LeasingStrategy,
            Attributes.Rank,
            Attributes.DealCreator,
            Attributes.DealOwner,
            Attributes.DevelopmentManager,
            Attributes.InvestorLp,
            Attributes.LenderPrivate,
            Attributes.LegalServices,
            Attributes.ProjectArchitect,
            Attributes.Surveyor,
            Attributes.EnvironmentalConsultant,
            Attributes.ParkingType,
        ],
        filterOptions: [FilterType.IsAnyOf, FilterType.IsNoneOf, FilterType.IsKnown, FilterType.IsUnknown],
    },
    {
        appliedToAttributes: [Attributes.Name, Attributes.Address, Attributes.City, Attributes.Description, Attributes.County, Attributes.Submarket],
        filterOptions: [FilterType.ContainsExactly, FilterType.DoesNotContainExactly, FilterType.IsKnown, FilterType.IsUnknown],
    },
    {
        appliedToAttributes: [
            Attributes.ZipCode,
            Attributes.Units,
            Attributes.Progress,
            Attributes.AcquisitionPrice,
            Attributes.TargetReturnOnCost,
            Attributes.TargetReturnOnEquity,
            Attributes.TargetProfitReturn,
            Attributes.IRR,
            Attributes.EquityMultiple,
            Attributes.IRRUsingProjectActuals,
            Attributes.EquityMultipleUsingProjectActuals,
        ],
        filterOptions: [
            FilterType.IsEqualTo,
            FilterType.IsNotEqualTo,
            FilterType.IsGreaterThan,
            FilterType.IsGreaterThanOrEqualTo,
            FilterType.IsLessThan,
            FilterType.IsLessThanOrEqualTo,
            FilterType.IsBetween,
            FilterType.IsKnown,
            FilterType.IsUnknown,
        ],
    },
    {
        appliedToAttributes: [Attributes.DateCreated],
        filterOptions: [
            FilterType.IsEqualTo,
            FilterType.IsNotEqualTo,
            FilterType.IsBefore,
            FilterType.IsAfter,
            FilterType.IsBetween,
            FilterType.IsAllTime,
            FilterType.IsThis,
            FilterType.IsNext,
            FilterType.IsLast,
        ],
    },
];

export enum AdditionalFieldType {
    Text = 'text',
    Textarea = 'textarea',
    MultiSelect = 'multi select',
    Select = 'select',
    Number = 'number',
    Date = 'date',
    Range = 'range',
    DateRange = 'date range',
}

export const getCustomAttributeAdditionalFields = (customOptions: IFilterOption[]): IAdditionalField[] => [
    {
        appliedToInputTypes: [CustomAttributeInputType.Text],
        appliedToFilters: [FilterType.ContainsExactly, FilterType.DoesNotContainExactly],
        type: AdditionalFieldType.Text,
    },
    {
        appliedToInputTypes: [CustomAttributeInputType.Number, CustomAttributeInputType.Amount],
        appliedToFilters: [
            FilterType.IsEqualTo,
            FilterType.IsNotEqualTo,
            FilterType.IsGreaterThan,
            FilterType.IsGreaterThanOrEqualTo,
            FilterType.IsLessThan,
            FilterType.IsLessThanOrEqualTo,
        ],
        type: AdditionalFieldType.Number,
    },
    {
        appliedToInputTypes: [CustomAttributeInputType.Number, CustomAttributeInputType.Amount],
        appliedToFilters: [FilterType.IsBetween],
        type: AdditionalFieldType.Range,
    },
    {
        appliedToInputTypes: [CustomAttributeInputType.Select, CustomAttributeInputType.MultiSelect],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: customOptions,
    },
];

export const getAdditionalFields: (params: {
    assetTypes: IFilterOption[];
    constructionTypes: IFilterOption[];
    markets: IFilterOption[];
    subprojectStages: IFilterOption[];
    orgUsers: IFilterOption[];
    developmentManagers: IFilterOption[];
    orgCompanies: IFilterOption[];
}) => IAdditionalField[] = ({
    assetTypes, constructionTypes, markets, subprojectStages, orgUsers, developmentManagers, orgCompanies,
}) => [
    {
        appliedToAttributes: [Attributes.Name, Attributes.Address, Attributes.City, Attributes.County, Attributes.Submarket],
        appliedToFilters: [FilterType.ContainsExactly, FilterType.DoesNotContainExactly],
        type: AdditionalFieldType.Text,
    },
    {
        appliedToAttributes: [Attributes.Description],
        appliedToFilters: [FilterType.ContainsExactly, FilterType.DoesNotContainExactly],
        type: AdditionalFieldType.Textarea,
    },
    {
        appliedToAttributes: [Attributes.State],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: stateListUS.map(state => ({ label: state.name, value: state.name })),
    },
    {
        appliedToAttributes: [
            Attributes.ZipCode,
            Attributes.Units,
            Attributes.Progress,
            Attributes.AcquisitionPrice,
            Attributes.TargetReturnOnCost,
            Attributes.TargetReturnOnEquity,
            Attributes.TargetProfitReturn,
            Attributes.IRR,
            Attributes.EquityMultiple,
            Attributes.IRRUsingProjectActuals,
            Attributes.EquityMultipleUsingProjectActuals,
        ],
        appliedToFilters: [
            FilterType.IsEqualTo,
            FilterType.IsNotEqualTo,
            FilterType.IsGreaterThan,
            FilterType.IsGreaterThanOrEqualTo,
            FilterType.IsLessThan,
            FilterType.IsLessThanOrEqualTo,
        ],
        type: AdditionalFieldType.Number,
    },
    {
        appliedToAttributes: [Attributes.DateCreated],
        appliedToFilters: [FilterType.IsEqualTo, FilterType.IsNotEqualTo, FilterType.IsBefore, FilterType.IsAfter],
        type: AdditionalFieldType.Date,
    },
    {
        appliedToAttributes: [
            Attributes.ZipCode,
            Attributes.Units,
            Attributes.Progress,
            Attributes.AcquisitionPrice,
            Attributes.TargetReturnOnCost,
            Attributes.TargetReturnOnEquity,
            Attributes.TargetProfitReturn,
            Attributes.IRR,
            Attributes.EquityMultiple,
            Attributes.IRRUsingProjectActuals,
            Attributes.EquityMultipleUsingProjectActuals,
        ],
        appliedToFilters: [FilterType.IsBetween],
        type: AdditionalFieldType.Range,
    },
    {
        appliedToAttributes: [Attributes.DateCreated],
        appliedToFilters: [FilterType.IsBetween],
        type: AdditionalFieldType.DateRange,
    },
    {
        appliedToAttributes: [Attributes.DateCreated],
        appliedToFilters: [FilterType.IsThis],
        type: AdditionalFieldType.Select,
        options: [
            { label: Period.Day, value: Period.Day },
            { label: Period.Week, value: Period.Week },
            { label: Period.WeekSoFar, value: Period.WeekSoFar },
            { label: Period.Month, value: Period.Month },
            { label: Period.MonthSoFar, value: Period.MonthSoFar },
            { label: Period.Quarter, value: Period.Quarter },
            { label: Period.QuarterSoFar, value: Period.QuarterSoFar },
            { label: Period.Year, value: Period.Year },
            { label: Period.YearSoFar, value: Period.YearSoFar },
        ],
    },
    {
        appliedToAttributes: [Attributes.DateCreated],
        appliedToFilters: [FilterType.IsNext, FilterType.IsLast],
        type: AdditionalFieldType.Select,
        options: [
            { label: Period.Day, value: Period.Day },
            { label: Period.Week, value: Period.Week },
            { label: Period.Month, value: Period.Month },
            { label: Period.Quarter, value: Period.Quarter },
            { label: Period.Year, value: Period.Year },
        ],
    },
    {
        appliedToAttributes: [Attributes.DealType],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: Object.values(DealType).map(type => ({
            label: type,
            value: DealTypeToId[type],
        })),
    },
    {
        appliedToAttributes: [Attributes.Phase],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: subprojectStages,
    },
    {
        appliedToAttributes: [Attributes.LeadSource],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: Object.values(LeadSource).map(source => ({
            label: source,
            value: LeadSourceToId[source],
        })),
    },
    {
        appliedToAttributes: [Attributes.LeasingStrategy],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: Object.values(LeasingStrategy).map(ls => ({
            label: ls,
            value: LeasingStrategyToId[ls],
        })),
    },
    {
        appliedToAttributes: [Attributes.Rank],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: Object.values(Rank).map(rank => ({
            label: rank,
            value: RankToId[rank],
        })),
    },
    {
        appliedToAttributes: [Attributes.Market],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: markets,
    },
    {
        appliedToAttributes: [Attributes.AssetClass],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: assetTypes,
    },
    {
        appliedToAttributes: [Attributes.ConstructionType],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: constructionTypes,
    },
    {
        appliedToAttributes: [
            Attributes.DealCreator,
            Attributes.DealOwner,
        ],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: orgUsers,
    },
    {
        appliedToAttributes: [
            Attributes.DevelopmentManager,
        ],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: developmentManagers,
    },
    {
        appliedToAttributes: [
            Attributes.InvestorLp,
            Attributes.LenderPrivate,
            Attributes.LegalServices,
            Attributes.ProjectArchitect,
            Attributes.Surveyor,
            Attributes.EnvironmentalConsultant,
        ],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: orgCompanies,
    },
    {
        appliedToAttributes: [Attributes.ParkingType],
        appliedToFilters: [FilterType.IsAnyOf, FilterType.IsNoneOf],
        type: AdditionalFieldType.MultiSelect,
        options: [
            { label: 'Surface', value: 'Surface' },
            { label: 'Structured', value: 'Structured' },
            { label: 'Underground', value: 'Underground' },
            { label: 'No Parking', value: 'No Parking' },
            { label: 'Trailer', value: 'Trailer' },
        ],
    },
];
